import { Button, Card, useToast } from "@nfp-tracker/components";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { supabase } from "../integrations/supabase/client";
import {
  acceptInstructorInvite,
  getInstructorInviteByToken,
} from "../integrations/supabase/instructor";

interface InviteData {
  id: string;
  email: string;
  owner_user_id: string;
  expires_at: string;
  accepted: boolean;
  sender?: {
    first_name: string;
    last_name: string;
  };
}

type InstructorInvite = {
  id: string;
  inviteeEmail: string;
  inviterName: string | null;
  expiresAt: string;
};

const INSTRUCTOR_ACCEPT_FAILURE_COPY: Record<string, string> = {
  invite_not_found: "This invitation is no longer available.",
  invite_expired: "This invitation has expired. Ask your client to send a new one.",
  email_mismatch:
    "This invitation was sent to a different email address. Sign in with that address to accept it.",
  not_an_instructor:
    "This account isn't set up as an instructor account. Contact support to have instructor access enabled, then accept the invitation.",
  instructor_profile_missing:
    "Finish setting up your instructor account first, then accept the invitation.",
};

const InviteAccept = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [error, setError] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [instructorInvite, setInstructorInvite] = useState<InstructorInvite | null>(null);
  const [acceptingInstructorInvite, setAcceptingInstructorInvite] = useState(false);
  const { toast } = useToast();

  const checkAuthAndLoadInvite = useCallback(async () => {
    try {
      console.info("[InviteLink] load-start", { token: token || null });

      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
      console.info("[InviteLink] auth-state", { isAuthenticated: Boolean(user) });

      // Spouse invites first: they are the released, common path and must never depend on the
      // instructor RPC existing. Only a token that is not a spouse invite falls through to the
      // instructor lookup below.
      const { data: inviteData, error: inviteError } = await supabase
        .rpc("get_invite_by_token", { invite_token: token });

      if (inviteError || !inviteData || inviteData.length === 0) {
        // Instructor invites live in the same table under a different link_type, so a token that
        // isn't a spouse invite may still be a valid chart-sharing invite. A database without
        // the instructor migration yields null here (the helper swallows "function not found").
        let instructorMatch: Awaited<ReturnType<typeof getInstructorInviteByToken>> = null;
        if (token) {
          try {
            instructorMatch = await getInstructorInviteByToken(token);
          } catch (instructorErr) {
            console.warn("[InviteLink] instructor-lookup-failed", {
              message: (instructorErr as { message?: string } | null)?.message || null,
            });
          }
        }

        if (instructorMatch) {
          if (instructorMatch.accepted) {
            setError("This invitation has already been accepted");
            return;
          }

          if (new Date(instructorMatch.expiresAt) < new Date()) {
            setError("This invitation has expired");
            return;
          }

          setInstructorInvite({
            id: instructorMatch.id,
            inviteeEmail: instructorMatch.inviteeEmail,
            inviterName: instructorMatch.inviterName,
            expiresAt: instructorMatch.expiresAt,
          });
          return;
        }

        console.warn("[InviteLink] invalid-token", {
          token: token || null,
          error: inviteError?.message || null,
        });
        setError("Invalid invitation link");
        return;
      }

      // RPC returns an array, get first item
      const invite = inviteData[0];

      const { data: senderProfile } = await supabase
        .from("user_accounts")
        .select("first_name, last_name")
        .eq("user_id", invite.owner_user_id)
        .maybeSingle();

      const inviteWithCouple = {
        ...invite,
        sender: senderProfile || undefined,
      };

      console.info("[InviteLink] invite-loaded", {
        inviteId: inviteWithCouple.id,
        ownerUserId: inviteWithCouple.owner_user_id,
        inviteeEmail: inviteWithCouple.email,
        expiresAt: inviteWithCouple.expires_at,
      });

      // Check if expired
      const expiresAt = new Date(inviteWithCouple.expires_at);
      if (expiresAt < new Date()) {
        console.warn("[InviteLink] expired", { inviteId: inviteWithCouple.id });
        setError("This invitation has expired");
        return;
      }

      // Check if already accepted
      if (inviteWithCouple.accepted) {
        console.warn("[InviteLink] already-accepted", { inviteId: inviteWithCouple.id });
        setError("This invitation has already been accepted");
        return;
      }

      setInvite(inviteWithCouple);
    } catch (err: unknown) {
      console.error(err);
      console.error("[InviteLink] load-failed", {
        token: token || null,
        message: (err as { message?: string } | null)?.message || null,
      });
      setError("Failed to load invitation");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuthAndLoadInvite();
  }, [checkAuthAndLoadInvite]);

  const handleAcceptInstructorInvite = async () => {
    if (!instructorInvite) return;

    setAcceptingInstructorInvite(true);
    try {
      const result = await acceptInstructorInvite(instructorInvite.id);

      if (result.ok) {
        toast({
          title: "Chart shared with you",
          description: "The client now appears in your client list.",
        });
        navigate(ROUTES.instructorClients, { replace: true });
        return;
      }

      // Only a provisioned instructor can finish setup — an account without the role has nothing
      // to fill in, so it gets the copy below instead of a dead-end form.
      if (result.reason === "instructor_profile_missing") {
        navigate(`${ROUTES.instructorSetup}?inviteId=${instructorInvite.id}`, { replace: true });
        return;
      }

      setError(
        (result.reason && INSTRUCTOR_ACCEPT_FAILURE_COPY[result.reason]) ||
          "We couldn't accept this invitation."
      );
    } catch (err) {
      console.error("[InviteLink] instructor-accept-failed", err);
      setError("We couldn't accept this invitation.");
    } finally {
      setAcceptingInstructorInvite(false);
    }
  };

  const inviteTokenParam = token ? `inviteToken=${encodeURIComponent(token)}` : "";
  const inviteEmailParam = invite?.email
    ? `email=${encodeURIComponent(invite.email)}`
    : instructorInvite?.inviteeEmail
      ? `email=${encodeURIComponent(instructorInvite.inviteeEmail)}`
      : "";
  const authQueryString = [inviteTokenParam, inviteEmailParam].filter(Boolean).join("&");
  const loginPath = authQueryString ? `${ROUTES.login}?${authQueryString}` : ROUTES.login;
  const signupPath = authQueryString ? `${ROUTES.signup}?${authQueryString}` : ROUTES.signup;
  const onboardingPath = inviteTokenParam ? `${ROUTES.onboarding}?${inviteTokenParam}` : ROUTES.onboarding;

  if (loading) {
    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
        <Card className="w-full max-w-md p-8 text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-2xl font-bold">Invalid Invitation</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => navigate(ROUTES.root)} className="w-full">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  if (instructorInvite) {
    const clientName = instructorInvite.inviterName || "A client";

    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
        <Card className="w-full max-w-md p-8 space-y-6 shadow-[var(--shadow-medium)]">
          <div className="text-center space-y-2">
            <CheckCircle className="h-12 w-12 text-primary mx-auto" />
            <h1 className="text-2xl font-bold">Chart sharing request</h1>
            <p className="text-muted-foreground">
              {clientName} would like to share their NFP Tracker chart with you as their
              instructor. You'll get read-only access to their observations, calendar, and cycle
              history.
            </p>
          </div>

          {isAuthenticated ? (
            <Button
              className="h-12 w-full"
              onClick={handleAcceptInstructorInvite}
              disabled={acceptingInstructorInvite}
            >
              {acceptingInstructorInvite ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Accept and view client
            </Button>
          ) : (
            <div className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                Sign in with {instructorInvite.inviteeEmail} to accept this invitation.
              </p>
              {/* Instructor accounts are provisioned by support (service role only), so there is
                  no self-serve signup to send an invitee to. */}
              <Button onClick={() => navigate(loginPath)} className="h-12 w-full">
                Sign in
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Don't have an instructor account yet? Contact support to get set up.
              </p>
            </div>
          )}
        </Card>
      </div>
    );
  }

  const senderName = invite?.sender
    ? `${invite.sender.first_name} ${invite.sender.last_name}`
    : "Your spouse";

  return (
    <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
      <Card className="w-full max-w-md p-8 space-y-6 shadow-[var(--shadow-medium)]">
        <div className="text-center space-y-2">
          <CheckCircle className="h-12 w-12 text-primary mx-auto" />
          <h1 className="text-2xl font-bold">You're Invited!</h1>
          <p className="text-muted-foreground">
            {senderName} has invited you to join their NFP Tracker couple's account
          </p>
        </div>

        {isAuthenticated ? (
          <div className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Continue to onboarding to finish account setup. You'll choose whether to accept or skip this invite at the end.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate(onboardingPath)}
                className="w-full h-12"
              >
                Continue Setup
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Create an account to continue. The invitation decision happens after setup.
            </p>
            <Button onClick={() => navigate(signupPath)} className="w-full h-12">
              Create Account
            </Button>
            <Button variant="outline" onClick={() => navigate(loginPath)} className="w-full h-12">
              Sign In
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InviteAccept;
